import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import {
	PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES,
	useDashboardShareActions,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { OnlineShareChannel } from 'src/app/pages/starterDashboardV2/shared/types';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';
import type { ShareAction } from 'src/app/shared/hooks/share';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

type UseSharePreviewModalResult = ModelHookResult<
	{
		shareAction?: ShareAction<OnlineShareChannel>;
	},
	{
		beforeShare: () => Promise<void>;
		afterShare: () => void;
		trackView: () => void;
	}
>;

// TODO: Thinking this would be a parameter in the future, but for now we only have Facebook
const SHARING_PLATFORM = 'facebook';
// Constants for tracking events
export const PRE_MADE_POST_MODAL_VIEW_EVENT_NAME = 'facebook_share_post_modal_view';
export const PRE_MADE_POST_MODAL_COMMON_PROPERTIES = {
	...PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES,
	location: 'facebook_post',
};
const CUSTOM_UTM_PARAMS = {
	utm_campaign: 'starter_dashboard',
	utm_source: 'share_petition',
	utm_term: 'starter_dashboard',
	utm_medium: 'facebook_post',
};
export const DEFAULT_SHARE_MESSAGE = 'Help us spread the word!';

export function useSharePreviewModal(
	onClose: () => void,
	readyToSharePost: ReadyToSharePostWithPlatform,
): UseSharePreviewModalResult {
	const i18n = useI18n();
	const track = useTracking();

	const channelActions = useDashboardShareActions(
		SHARING_PLATFORM,
		[readyToSharePost],
		[PRE_MADE_POST_MODAL_COMMON_PROPERTIES],
		[CUSTOM_UTM_PARAMS],
	);

	const beforeShare = useCallback(async () => {
		if (channelActions.length === 0) {
			return;
		}
		await copyToClipboard(readyToSharePost.message || DEFAULT_SHARE_MESSAGE, i18n);
	}, [channelActions, i18n, readyToSharePost.message]);

	const afterShare = useCallback(() => {
		if (channelActions.length === 0) {
			return;
		}
		onClose();
	}, [channelActions, onClose]);

	return {
		data: {
			shareAction: channelActions.length > 0 ? channelActions[0] : undefined,
		},
		actions: {
			beforeShare,
			afterShare,
			trackView: useCallback(() => {
				void track(PRE_MADE_POST_MODAL_VIEW_EVENT_NAME, PRE_MADE_POST_MODAL_COMMON_PROPERTIES);
			}, [track]),
		},
	};
}
